import { useEffect, useState } from 'react';

/**
 * THE WATCH LINE (#621) — a standing, NON-ALARMING count + list of MONITORED &
 * UNFOUGHT out-of-control fires near communities, rendered under the layer panel.
 *
 * WHY (founder-approved, de-flood ruling): escalation is a CHANGE trigger, but a duty
 * officer's decision is a STATE decision — a big unfought fire near a community is
 * dangerous AT REST and may never trip a gate, and findability ≠ awareness at 3am. So
 * this surfaces the count without adding a single map icon / layer / pref (the default
 * COP view is UNCHANGED). "Unfought" = an OC fire whose response posture the ladder scores
 * on the wide radius: Monitored, or an unknown/not-reported posture (never Full/Modified —
 * crews engaged). Data: GET /api/oc-fire-watch (national, freshness-gated).
 */

export interface OcFireWatch {
  unfought_total: number;
  over_100ha_near_community: number;
  largest: {
    fire_id: string | null;
    hectares: number | null;
    community_name: string;
    distance_m: number;
    compass: string;
  } | null;
}

/** Pure formatter (unit-tested) — returns the watch-line text, or null when there is
 *  nothing to report (zero unfought fires → the line is omitted, never a hollow "0"). */
export function formatOcFireWatch(
  w: OcFireWatch | null,
): { headline: string; detail: string | null } | null {
  if (!w || w.unfought_total <= 0) return null;
  const fires = `${w.unfought_total} ${w.unfought_total === 1 ? 'fire' : 'fires'}`;
  const near = `${w.over_100ha_near_community} over 100 ha within 50 km of a community`;
  const headline = `Monitored & unfought: ${fires} · ${near}`;
  let detail: string | null = null;
  if (w.largest && w.largest.community_name) {
    const ha =
      typeof w.largest.hectares === 'number'
        ? `${Math.round(w.largest.hectares).toLocaleString()} ha`
        : 'largest';
    const km = Math.round(w.largest.distance_m / 1000);
    detail = `Largest near a community: ${ha}, ${km} km ${w.largest.compass} of ${w.largest.community_name}`;
  }
  return { headline, detail };
}

/** Poll cadence — a standing awareness surface, not an alert; slow refresh (10 min). */
const POLL_MS = 10 * 60 * 1000;

export function OcFireWatchLine(): JSX.Element | null {
  const [watch, setWatch] = useState<OcFireWatch | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await fetch('/api/oc-fire-watch');
        if (!res.ok) return;
        const body = (await res.json()) as OcFireWatch;
        if (!cancelled) setWatch(body);
      } catch {
        /* offline / 5xx → keep the last value; this is ambient, never blocking */
      }
    };
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const text = formatOcFireWatch(watch);
  if (!text) return null;

  return (
    <div
      data-testid="oc-fire-watch-line"
      className="mt-2 rounded border border-white/10 bg-white/[0.03] px-2.5 py-2 text-[11px] leading-snug text-slate-400"
      // Ambient, non-alarming: muted slate, no red, no pulse (founder de-flood ruling).
      title="Monitored & unfought out-of-control fires near communities — awareness, not an alert. They surface on the map only via the normal Threat Level gate."
    >
      <div className="flex items-center gap-1.5">
        {/* A quiet binoculars glyph = "being observed" (CIFFC's monitored idiom), never a flame. */}
        <span aria-hidden className="text-slate-500">
          ⦿
        </span>
        <span className="font-medium text-slate-300">{text.headline}</span>
      </div>
      {text.detail && <div className="mt-0.5 pl-4 text-slate-500">{text.detail}</div>}
    </div>
  );
}
